import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/verify-reset-token`, {
        params: { token }
      });
      
      if (response.data.valid) {
        setTokenValid(true);
        setUserInfo(response.data);
      } else {
        setTokenValid(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      setTokenValid(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API_BASE}/api/auth/reset-password`, {
        token,
        new_password: formData.newPassword
      });
      
      setResetSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset failed:', error);
      alert(error.response?.data?.detail || 'Password reset failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (tokenValid === null) {
    return (
      <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
        <div className="max-w-md mx-auto px-6 py-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-turquoise-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying reset token...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
        <div className="max-w-md mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={40} className="text-red-400" />
            </div>
            <h1 className="display-large mb-4">
              Invalid <span className="text-red-400">Reset Link</span>
            </h1>
            <p className="body-large text-gray-300 mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
          </div>

          <div className="space-y-3">
            <Link to="/forgot-password">
              <Button className="btn-primary-enhanced w-full">
                Request New Reset Link
              </Button>
            </Link>
            <Link to="/login">
              <Button className="btn-secondary w-full">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
        <div className="max-w-md mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-green-400" />
            </div>
            <h1 className="display-large mb-4">
              Password <span className="turquoise-text-gradient">Reset Complete</span>
            </h1>
            <p className="body-large text-gray-300 mb-6">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
          </div>

          <div className="bg-green-500/10 border border-green-500/20 rounded-16 p-6 mb-6 text-center">
            <p className="text-green-400 mb-3">✅ Password Updated Successfully</p>
            <p className="text-gray-300 text-sm mb-4">
              Redirecting you to the sign-in page in 3 seconds...
            </p>
            <Link to="/login">
              <Button className="btn-primary-enhanced w-full">
                Sign In Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-md mx-auto px-6 py-8">
        <Link to="/login">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Sign In
          </Button>
        </Link>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={40} className="text-turquoise-400" />
          </div>
          <h1 className="display-large mb-4">
            Reset Your <span className="turquoise-text-gradient">Password</span>
          </h1>
          <p className="body-large text-gray-300">
            {userInfo?.user_name && `Hi ${userInfo.user_name}! `}
            Create a new secure password for your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 mb-6">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Lock size={16} className="inline mr-2" />
                New Password
              </label>
              <input
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                required
                minLength="6"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Enter new password (min. 6 characters)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Lock size={16} className="inline mr-2" />
                Confirm New Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                minLength="6"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none transition-colors"
                placeholder="Confirm your new password"
              />
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="btn-primary-enhanced w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Updating Password...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Update Password
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h3 className="text-blue-400 font-medium mb-2">🔐 Password Requirements</h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• At least 6 characters long</li>
            <li>• Should be unique and not used elsewhere</li>
            <li>• Mix of letters, numbers, and symbols recommended</li>
            <li>• Avoid common passwords or personal information</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;